import { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, errMsg } from "@/lib/api";
import { inputCls, btnPrimary, Skeleton } from "@/components/portal/ui";
import { Switch } from "@/components/ui/switch";
import { AdminTitle } from "@/pages/admin/AdminLayout";
import { ImageField } from "@/pages/admin/ResourceManager";

const GATEWAYS = [
  ["manual", "Transfer Manual", "Selalu aktif. Jemaat unggah bukti, admin verifikasi."],
  ["midtrans_enabled", "Midtrans", "QRIS & Virtual Account otomatis via Snap."],
];

function Row({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{label}</span>
      <div className="mt-2">{children}</div>
      {hint && <p className="text-xs text-slate-400 mt-1.5">{hint}</p>}
    </label>
  );
}

function NotifySettings() {
  const [n, setN] = useState(null);
  const [testTarget, setTestTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/notify/settings").then((r) => setN(r.data));
  useEffect(() => { load(); }, []);
  if (!n) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/notify/settings", { token: n.token || null, enabled: n.enabled });
      setN(data);
      toast.success("Pengaturan notifikasi disimpan");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const testWa = async () => {
    try {
      const { data } = await api.post("/admin/notify/test-wa", { target: testTarget, message: "Tes notifikasi WhatsApp GIM Church. Jika pesan ini sampai, integrasi aktif." });
      data.sent ? toast.success("Pesan terkirim (queued)") : toast.error(`Gagal: ${data.status}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const testPush = async () => {
    try {
      await api.post("/admin/notify/test-push");
      toast.success("Push test dikirim ke perangkat Anda");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="mt-6 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="settings-notifications">
      <div className="flex items-center justify-between">
        <p className="font-extrabold text-slate-900">Notifikasi WhatsApp & Web Push</p>
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-600">Aktif <Switch checked={!!n.enabled} onCheckedChange={(v) => setN({ ...n, enabled: v })} data-testid="wa-enabled-switch" /></span>
      </div>
      <Row label="Token Fonnte (WhatsApp Gateway)" hint={n.configured ? `Tersimpan (${n.token_masked}). Kosongkan untuk tidak mengubah.` : "Daftar di fonnte.com → buat device → scan QR → salin token. Pengingat event H-1 & ulang tahun terkirim otomatis tiap 06:30 WITA."}>
        <input type="password" className={inputCls} value={n.token || ""} onChange={(e) => setN({ ...n, token: e.target.value })} placeholder="Token device Fonnte" data-testid="wa-token-input" />
      </Row>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={save} disabled={busy} className={`${btnPrimary} !py-2.5 text-sm`} data-testid="notify-save-button">{busy ? "Menyimpan…" : "Simpan Notifikasi"}</button>
        <div className="flex gap-2 items-center">
          <input className={`${inputCls} !w-44 !py-2 text-sm`} placeholder="08xx uji WA" value={testTarget} onChange={(e) => setTestTarget(e.target.value)} data-testid="wa-test-target" />
          <button type="button" onClick={testWa} className="text-xs font-bold bg-emerald-500 text-white rounded-xl px-4 py-2.5" data-testid="wa-test-button">Tes WA</button>
        </div>
        <button type="button" onClick={testPush} className="text-xs font-bold bg-slate-900 text-white rounded-xl px-4 py-2.5" data-testid="push-test-button">Tes Web Push ({n.push_subscriptions} perangkat)</button>
      </div>
    </div>
  );
}

function EmailSettings() {
  const [e, setE] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/email-settings").then((r) => setE(r.data)); }, []);
  if (!e) return null;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/email-settings", { reply_to: e.reply_to || null });
      setE(data);
      toast.success("Pengaturan email disimpan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-6 bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="settings-email-gateway">
      <p className="font-extrabold text-slate-900">Email Gateway</p>
      <Row label="Nama Pengirim" hint="Dikunci platform demi keamanan & tingkat keterkiriman — tidak dapat diubah dari sini.">
        <input readOnly disabled className={`${inputCls} bg-slate-50 text-slate-400 cursor-not-allowed`} value={e.from_name} data-testid="email-from-name-input" />
      </Row>
      <Row label="Alamat Reply-To (opsional)" hint="Saat jemaat membalas email dari sistem (verifikasi, reset password, dll), balasan akan masuk ke alamat ini.">
        <input type="email" className={inputCls} placeholder="pastoral@gimchurch.id" value={e.reply_to || ""} onChange={(ev) => setE({ ...e, reply_to: ev.target.value })} data-testid="email-reply-to-input" />
      </Row>
      <button type="button" onClick={save} disabled={busy} className={`${btnPrimary} !py-2.5 text-sm`} data-testid="email-settings-save-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Email Gateway"}</button>
    </div>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
  }, []);
  if (!s) return <Skeleton className="h-96" />;
  const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
  const webhook = `${API}/payments/midtrans/notification`;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data);
      toast.success("Pengaturan disimpan");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
    <form onSubmit={save} data-testid="admin-settings">
      <AdminTitle eyebrow="Sistem" title="Pengaturan Pembayaran">
        <button disabled={saving} data-testid="settings-save-button" className={btnPrimary}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Pengaturan"}</button>
      </AdminTitle>
      <div className="grid md:grid-cols-3 gap-4">
        {GATEWAYS.map(([k, l, d]) => {
          const on = k === "manual" || !!s[k];
          return (
            <div key={k} data-testid={`gateway-${k}`} className={`rounded-3xl p-6 border transition duration-300 ${on ? "bg-blue-600 border-blue-600 text-white shadow-md" : "bg-white border-slate-100 shadow-sm"}`}>
              <div className="flex items-center justify-between"><p className="font-extrabold text-lg">{l}</p>
                {k !== "manual" && <Switch checked={on} onCheckedChange={(v) => setS({ ...s, [k]: v })} data-testid={`gateway-toggle-${k}`} />}</div>
              <p className={`text-sm mt-1 ${on ? "text-blue-100" : "text-slate-500"}`}>{d}</p>
              <p className={`mt-4 text-[11px] font-bold uppercase tracking-widest ${on ? "text-white" : "text-slate-400"}`}>{on ? "Aktif" : "Nonaktif"}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="settings-midtrans">
          <div className="flex items-center justify-between"><p className="font-extrabold text-slate-900">Kredensial Midtrans</p>
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-600">Production <Switch checked={!!s.midtrans_production} onCheckedChange={(v) => setS({ ...s, midtrans_production: v })} data-testid="midtrans-production-switch" /></span></div>
          <Row label="Server Key" hint={s.midtrans_server_key_set ? "Tersimpan. Kosongkan untuk tidak mengubah." : "Dari Midtrans Dashboard → Settings → Access Keys"}><input data-testid="midtrans-server-key-input" type="password" className={inputCls} value={s.midtrans_server_key || ""} onChange={set("midtrans_server_key")} placeholder="SB-Mid-server-…" /></Row>
          <Row label="Client Key"><input data-testid="midtrans-client-key-input" className={inputCls} value={s.midtrans_client_key || ""} onChange={set("midtrans_client_key")} placeholder="SB-Mid-client-…" /></Row>
          <Row label="Notification URL (Webhook)" hint="Salin ke Midtrans → Settings → Payment → Notification URL">
            <div className="flex gap-2"><input readOnly className={`${inputCls} text-xs`} value={webhook} data-testid="midtrans-webhook-url" /><button type="button" onClick={() => { navigator.clipboard?.writeText(webhook); toast("Disalin"); }} className="shrink-0 w-12 rounded-xl bg-slate-100 grid place-items-center"><Copy className="w-4 h-4" /></button></div>
          </Row>
        </div>
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-sm border border-slate-100 space-y-4" data-testid="settings-bank">
          <p className="font-extrabold text-slate-900">Rekening Transfer Manual</p>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Bank"><input data-testid="bank-name-input" className={inputCls} value={s.bank_name || ""} onChange={set("bank_name")} /></Row>
            <Row label="No. Rekening"><input data-testid="bank-account-input" className={inputCls} value={s.bank_account || ""} onChange={set("bank_account")} /></Row>
          </div>
          <Row label="Atas Nama"><input data-testid="bank-holder-input" className={inputCls} value={s.bank_holder || ""} onChange={set("bank_holder")} /></Row>
          <Row label="QRIS Statis (opsional)" hint="Unggah foto/screenshot kode QRIS toko/rekening gereja agar jemaat bisa scan langsung saat transfer manual.">
            <ImageField value={s.qris_image_url} onChange={(v) => setS({ ...s, qris_image_url: v })} id="qris_image" />
          </Row>
        </div>
      </div>
    </form>
    <NotifySettings />
    <EmailSettings />
    </>
  );
}
